import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {}
      if (attempts >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 noise-overlay bg-[#040814]">
      <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[140px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="relative z-10 max-w-md w-full text-center rounded-3xl border border-white/[0.08] bg-[#0A101F] p-10" data-testid="payment-success-card">
        {state === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-blue-400 animate-spin" />
            <h1 className="font-head text-3xl tracking-tight mt-6">{t("payment.confirming")}</h1>
            <p className="text-slate-400 mt-3">{t("payment.confirmingSub")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-blue-400" />
            <h1 className="font-head text-3xl tracking-tight mt-6" data-testid="payment-success-title">{t("payment.paidTitle")}</h1>
            <p className="text-slate-400 mt-3">{t("payment.paidSub")}</p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle size={56} className="mx-auto text-slate-500" />
            <h1 className="font-head text-3xl tracking-tight mt-6">{state === "timeout" ? t("payment.timeoutTitle") : t("payment.errorTitle")}</h1>
            <p className="text-slate-400 mt-3">{state === "timeout" ? t("payment.timeoutSub") : t("payment.errorSub")}</p>
          </>
        )}

        <Link to="/" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-white text-[#040814] font-semibold hover:bg-blue-500 hover:text-white transition-colors duration-300" data-testid="back-home-btn">
          <ArrowLeft size={16} /> {t("payment.backHome")}
        </Link>
      </motion.div>
    </div>
  );
}
